import html2canvas from "html2canvas";
import logoIcon from "../assets/images/logo-twist.png";
import type { Contact } from "../types/contact";
import {
  STORY_WIDTH,
  STORY_HEIGHT,
  STORY_SIDE_PADDING,
  STORY_TOP_PADDING,
  STORY_BOTTOM_PADDING,
} from "./mediaExportUtils";

export const downloadCampaignSummaryAsImage = async (
  cardElement: HTMLElement,
) => {
  try {
    // Create a container with mobile story aspect ratio (9:16)
    const container = document.createElement("div");
    container.style.position = "absolute";
    container.style.left = "-9999px";
    container.style.top = "-9999px";
    container.style.backgroundColor = "white";
    container.style.width = "450px";
    container.style.height = "800px";
    container.style.display = "flex";
    container.style.flexDirection = "column";
    container.style.alignItems = "center";
    container.style.justifyContent = "center";
    container.style.padding = "40px";
    container.style.boxSizing = "border-box";
    container.style.fontFamily = "system-ui, -apple-system, sans-serif";

    // Create logo element
    const logoContainer = document.createElement("div");
    logoContainer.style.display = "flex";
    logoContainer.style.justifyContent = "center";
    logoContainer.style.marginBottom = "30px";

    const logoImg = document.createElement("img");
    logoImg.src = logoIcon;
    logoImg.style.width = "150px";
    logoImg.style.height = "auto";
    logoImg.alt = "Logo";

    logoContainer.appendChild(logoImg);

    // Clone the card element
    const clonedCard = cardElement.cloneNode(true) as HTMLElement;

    // Remove any blur effects from the cloned element
    const blurElements = clonedCard.querySelectorAll(".blur-background");
    blurElements.forEach((el) => el.remove());

    // Clean up visibility for export
    const optionsContainer = clonedCard.querySelector(
      ".options-container",
    ) as HTMLElement;
    if (optionsContainer) {
      optionsContainer.style.maxHeight = "none";
      optionsContainer.style.overflow = "visible";
    }

    // Make the card fit the container width
    clonedCard.style.width = "100%";
    clonedCard.style.margin = "0";
    clonedCard.style.borderRadius = "12px";
    clonedCard.style.overflow = "hidden";
    clonedCard.style.boxShadow = "0 10px 30px rgba(0,0,0,0.1)";

    // Append elements to container
    container.appendChild(logoContainer);
    container.appendChild(clonedCard);

    // Add to DOM temporarily
    document.body.appendChild(container);

    // Wait for logo to load
    await new Promise((resolve) => {
      logoImg.onload = resolve;
      if (logoImg.complete) resolve(undefined);
    });

    // Capture the container as canvas
    const canvas = await html2canvas(container, {
      backgroundColor: "white",
      useCORS: true,
      allowTaint: true,
      logging: false,
    });

    // Remove the temporary container
    document.body.removeChild(container);

    // Convert canvas to blob and download
    canvas.toBlob((blob) => {
      if (blob) {
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "campaign-summary.png";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    }, "image/png");
  } catch (error) {
    console.error("Error downloading campaign summary:", error);
  }
};

export const downloadQuestionAsImage = async (
  element: HTMLElement,
  id: string = "card",
) => {
  await downloadQuestionWithBackground(element, "#000000", id, "center");
};

export const downloadContactsAsExcel = (contacts: Contact[]) => {
  try {
    const currentDate = new Date().toLocaleDateString("he-IL");
    const totalContacts = contacts.length;

    const title = "דוח אנשי קשר";
    const summary = `תאריך יצירת הדוח: ${currentDate}`;
    const count = `סה"כ אנשי קשר: ${totalContacts}`;

    const headers = ["שם מלא", "מספר טלפון", "תאריך הצטרפות", "מקור הצטרפות"];

    const csvContent = [
      `"${title}"`,
      `"${summary}"`,
      `"${count}"`,
      "",
      headers.join(","),
      ...contacts.map((contact) =>
        [
          `"${contact.name || "לא צוין"}"`,
          `"${contact.phone || "לא צוין"}"`,
          `"${contact.joinDate || "לא צוין"}"`,
          `"${contact.source || "לא צוין"}"`,
        ].join(","),
      ),
      "",
      `"דוח נוצר על ידי מערכת twist"`,
    ].join("\n");

    const BOM = "\uFEFF";
    const csvWithBOM = BOM + csvContent;

    const blob = new Blob([csvWithBOM], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `אנשי-קשר-${currentDate.replace(/\//g, "-")}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Error downloading contacts as Excel:", error);
  }
};
export const downloadQuestionWithBackground = async (
  element: HTMLElement,
  background: string,
  id: string = "card",
  cardPosition: "center" | "top" = "center",
) => {
  try {
    const container = document.createElement("div");
    container.style.position = "absolute";
    container.style.left = "-9999px";
    container.style.top = "-9999px";
    container.style.width = `${STORY_WIDTH}px`;
    container.style.height = `${STORY_HEIGHT}px`;
    container.style.display = "flex";
    container.style.flexDirection = "column";
    container.style.alignItems = "center";
    container.style.justifyContent =
      cardPosition === "top" ? "flex-start" : "center";
    container.style.paddingTop =
      cardPosition === "top" ? `${STORY_TOP_PADDING}px` : `${STORY_SIDE_PADDING}px`;
    container.style.paddingLeft = `${STORY_SIDE_PADDING}px`;
    container.style.paddingRight = `${STORY_SIDE_PADDING}px`;
    container.style.paddingBottom =
      cardPosition === "top" ? "0" : `${STORY_BOTTOM_PADDING}px`;
    container.style.boxSizing = "border-box";

    if (background.includes("url")) {
      container.style.background = background;
      container.style.backgroundSize = "cover";
      container.style.backgroundPosition = "center";
    } else {
      container.style.background = background;
    }

    const clonedElement = element.cloneNode(true) as HTMLElement;

    // Sync and replace inputs/textareas with spans for better rendering
    const originalInputs = element.querySelectorAll("input, textarea");
    const clonedInputs = clonedElement.querySelectorAll("input, textarea");
    originalInputs.forEach((input, index) => {
      const clonedInput = clonedInputs[index] as
        | HTMLInputElement
        | HTMLTextAreaElement;
      if (clonedInput) {
        const val = (input as HTMLInputElement | HTMLTextAreaElement).value;
        const span = document.createElement("span");
        span.textContent = val || (input as HTMLInputElement).placeholder || "";

        // Match styles
        const style = window.getComputedStyle(input);
        span.style.display = "inline-block";
        span.style.width = "100%";
        span.style.textAlign = style.textAlign || "center";
        span.style.color = style.color;
        span.style.fontSize = style.fontSize;
        span.style.fontFamily = style.fontFamily;
        span.style.fontWeight = style.fontWeight;
        span.style.lineHeight = style.lineHeight;
        span.style.wordBreak = "break-word";
        span.style.whiteSpace = "pre-wrap";

        if (clonedInput.parentNode) {
          clonedInput.parentNode.replaceChild(span, clonedInput);
        }
      }
    });

    clonedElement.style.margin = "0";
    clonedElement.style.width = "100%";
    clonedElement.style.background = "transparent";
    clonedElement.style.boxShadow = "none";

    container.appendChild(clonedElement);
    document.body.appendChild(container);

    // After render, position the footer so it is vertically centered between
    // the bottom edge of the card and the bottom edge of the full 800px image.
    if (cardPosition === "top") {
      const cardContainer = clonedElement.querySelector(
        ".question-card-container",
      ) as HTMLElement | null;
      const footerWrapper = clonedElement.querySelector(
        ".footer-wrapper",
      ) as HTMLElement | null;

      if (cardContainer && footerWrapper) {
        // Reset any CSS-set spacing so we control positioning entirely.
        footerWrapper.style.paddingTop = "0";
        footerWrapper.style.paddingBottom = "0";
        footerWrapper.style.marginTop = "0";

        // Force synchronous reflow.
        void container.offsetHeight;

        const cardHeight = cardContainer.offsetHeight;
        const footerHeight = footerWrapper.offsetHeight;

        // Space from card bottom to image bottom (the full 800px minus the
        // top padding and the card itself).
        const spaceBelow = STORY_HEIGHT - STORY_TOP_PADDING - cardHeight;
        const centerOffset = Math.max(0, (spaceBelow - footerHeight) / 2);

        footerWrapper.style.marginTop = `${centerOffset}px`;
      }
    }

    const canvas = await html2canvas(container, {
      backgroundColor: null,
      useCORS: true,
      allowTaint: true,
      logging: false,
      scale: 2,
    });

    document.body.removeChild(container);

    await new Promise<void>((resolve) => {
      canvas.toBlob((blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = `question-bg-${id}.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        }
        resolve();
      }, "image/png");
    });
  } catch (error) {
    console.error("Error downloading question with background:", error);
  }
};
